import { useState, useMemo } from 'react';
export type SortDirection = 'asc' | 'desc' | null;
export interface SortConfig {
  key: string;
  direction: SortDirection;
}
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
}
export function useSortableData<T>(items: T[], defaultConfig?: SortConfig) {
  const [sortConfig, setSortConfig] = useState<SortConfig>(defaultConfig || {
    key: '',
    direction: null
  });
  const sortedItems = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return items;
    return [...items].sort((a, b) => {
      const aVal = getNestedValue(a, sortConfig.key);
      const bVal = getNestedValue(b, sortConfig.key);

      // Handle nulls/undefined
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortConfig.direction === 'asc' ? 1 : -1;
      if (bVal == null) return sortConfig.direction === 'asc' ? -1 : 1;

      // Numeric comparison
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // Date comparison
      if (aVal instanceof Date && bVal instanceof Date) {
        return sortConfig.direction === 'asc' ? aVal.getTime() - bVal.getTime() : bVal.getTime() - aVal.getTime();
      }

      // String comparison
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [items, sortConfig]);
  const requestSort = (key: string) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        if (prev.direction === 'asc') return {
          key,
          direction: 'desc' as SortDirection
        };
        if (prev.direction === 'desc') return {
          key: '',
          direction: null
        };
      }
      return {
        key,
        direction: 'asc' as SortDirection
      };
    });
  };
  const getSortIndicator = (key: string): string => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };
  const isSorted = (key: string): SortDirection => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction;
  };
  return {
    sortedItems,
    sortConfig,
    requestSort,
    getSortIndicator,
    isSorted
  };
}